import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLElement} panel
 * @property {HTMLButtonElement} closeButton
 */

/**
 * Hamburger trigger + full-screen menu overlay (snippets/mobile-menu.liquid), used in place of
 * Horizon's native header-drawer when the "Use a single menu button" setting is on. The panel is
 * a `popover="manual"` element, promoted to the browser's top layer on open/close — this
 * component renders inside #header-component, whose `contain: layout style`
 * (sections/header.liquid) creates a containing block for position:fixed descendants; without
 * the top layer, that would trap this full-screen panel to the header's own small bounding box
 * instead of the real viewport. See the snippet's own doc comment for more.
 *
 * Full-screen and opaque, so — unlike <unified-mega-menu> (its desktop counterpart) — this needs
 * no backdrop, but does need a focus trap and scroll lock, since it behaves like a modal.
 *
 * @extends {Component<Refs>}
 */
class MobileMenu extends Component {
  requiredRefs = ['trigger', 'panel', 'closeButton'];

  #abortController = new AbortController();
  #mobileQuery = window.matchMedia('(max-width: 749px)');

  /**
   * Native top-layer support. Not `typeof showPopover`: on iOS 16 and older, popover-polyfill.js
   * installs its own showPopover, which can't reach the top layer. CSS.supports isn't polyfilled.
   */
  #supportsPopover = CSS.supports('selector(:popover-open)');

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    this.#mobileQuery.addEventListener('change', this.#repositionSearchButton, { signal });
    this.#repositionSearchButton();
  }

  #repositionSearchButton = () => {
    const searchButton = document.querySelector('.header__columns search-button');
    if (!(searchButton instanceof HTMLElement)) return;
    searchButton.style.gridArea = this.#mobileQuery.matches ? 'rightA' : '';
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#setHeaderContainment(true);
    unlockScroll(this);
    removeTrapFocus();
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  toggle = () => {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  open = () => {
    if (this.isOpen) return;
    this.setAttribute('open', '');
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    this.refs.panel.removeAttribute('inert');
    this.#showPanel();
    lockScroll(this);
    trapFocus(this.refs.panel);
    this.refs.closeButton.focus();
  };

  close = () => {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.refs.panel.setAttribute('inert', '');
    if (this.#supportsPopover && this.refs.panel.matches(':popover-open')) {
      this.refs.panel.hidePopover();
    }
    // After the 300ms slide-out (snippets/mobile-menu.liquid): re-containing mid-animation would
    // snap the sliding panel back into the header's box.
    setTimeout(() => {
      if (!this.isOpen) this.#setHeaderContainment(true);
    }, 300);
    unlockScroll(this);
    removeTrapFocus();
    this.refs.trigger.focus();
  };

  /**
   * Lifts the header's containment on every open, not only when the top layer is unavailable:
   * Safari 26 promotes the panel to the top layer (:popover-open is true) yet still sizes it
   * against #header-component's `contain: layout` box, so it opens 61px tall — only its top bar
   * shows. With nothing containing it, the panel can only size against the viewport.
   */
  #showPanel() {
    this.#setHeaderContainment(false);
    if (!this.#supportsPopover) return;
    try {
      this.refs.panel.showPopover();
    } catch {
      // Lifted containment above already keeps the panel full-screen.
    }
  }

  /**
   * Lifts (false) or restores (true) #header-component's `contain`, so the panel's position: fixed
   * and percentage sizes resolve against the viewport.
   * @param {boolean} contained
   */
  #setHeaderContainment(contained) {
    const header = this.closest('#header-component');
    if (!(header instanceof HTMLElement)) return;
    header.style.contain = contained ? '' : 'none';
  }

  openSearch = () => {
    this.close();
    const dialog = /** @type {(HTMLElement & { showDialog?: () => void }) | null} */ (
      document.getElementById('search-modal')
    );
    dialog?.showDialog?.();
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    this.close();
  };
}

if (!customElements.get('mobile-menu')) {
  customElements.define('mobile-menu', MobileMenu);
}
